// Pupil Parents & Carers Tab - SAT V4 Zone
import { DsButton, DsCard, DsIcon, DsTypography } from '@ice-web-app/designSystem';
import {
  Avatar,
  Box,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
} from '@mui/material';
import type { FC } from 'react';
import { useState } from 'react';
import { mockParentCarers } from '../../../../lib/mockData';
import type { ParentCarer, Pupil } from '../../../../types';

const TITLES = ['Mr', 'Mrs', 'Ms', 'Miss', 'Dr'];
const RELATIONSHIPS = ['Father', 'Mother', 'Guardian', 'Grandparent', 'Step-parent', 'Other'];

const getDefaultContact = (): Omit<ParentCarer, 'id' | 'pupilIds'> => ({
  title: 'Mr',
  firstName: '',
  lastName: '',
  relationship: 'Father',
  email: '',
  phone: '',
  workPhone: '',
  isPrimaryContact: false,
  isEmergencyContact: true,
  canPickup: true,
  receivesReports: true,
});

interface ParentsCarersTabProps {
  pupil: Pupil;
  onUpdate?: (updates: Partial<Pupil>) => void;
}

const ParentsCarersTab: FC<ParentsCarersTabProps> = ({ pupil, onUpdate }) => {
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [contactForm, setContactForm] = useState<Omit<ParentCarer, 'id' | 'pupilIds'>>(getDefaultContact());
  const [editingContactId, setEditingContactId] = useState<string | null>(null);

  const handleOpenAdd = () => {
    setContactForm(getDefaultContact());
    setAddModalOpen(true);
  };

  const handleOpenEdit = (contact: ParentCarer) => {
    setEditingContactId(contact.id);
    setContactForm({
      title: contact.title,
      firstName: contact.firstName,
      lastName: contact.lastName,
      relationship: contact.relationship,
      email: contact.email,
      phone: contact.phone,
      workPhone: contact.workPhone || '',
      isPrimaryContact: contact.isPrimaryContact,
      isEmergencyContact: contact.isEmergencyContact,
      canPickup: contact.canPickup,
      receivesReports: contact.receivesReports,
    });
    setEditModalOpen(true);
  };

  const handleAddContact = () => {
    if (contactForm.firstName && contactForm.lastName && contactForm.email) {
      // Mock add - in real app would call API
      setAddModalOpen(false);
      setContactForm(getDefaultContact());
    }
  };

  const handleEditContact = () => {
    if (editingContactId && contactForm.firstName && contactForm.lastName) {
      // Mock edit - in real app would call API
      setEditModalOpen(false);
      setEditingContactId(null);
      setContactForm(getDefaultContact());
    }
  };
  const parentCarers = mockParentCarers.filter((p) => pupil.parentCarerIds?.includes(p.id));
  const primaryContact = parentCarers.find((p) => p.isPrimaryContact);
  const otherContacts = parentCarers.filter((p) => !p.isPrimaryContact);

  const renderContactCard = (contact: ParentCarer, isPrimary = false) => (
    <DsCard
      sx={{
        p: 3,
        borderLeft: 4,
        borderColor: isPrimary ? 'primary.main' : 'divider',
      }}
    >
      <Stack spacing={2}>
        <Stack direction="row" spacing={2} alignItems="flex-start">
          <Avatar sx={{ bgcolor: isPrimary ? 'primary.main' : 'grey.400', width: 48, height: 48 }}>
            {contact.firstName[0]}
            {contact.lastName[0]}
          </Avatar>
          <Box sx={{ flex: 1 }}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <DsTypography variant="h6" fontWeight={600}>
                {contact.title} {contact.firstName} {contact.lastName}
              </DsTypography>
              {isPrimary && <Chip label="Primary Contact" size="small" color="primary" />}
            </Stack>
            <DsTypography variant="body2" color="text.secondary">
              {contact.relationship}
            </DsTypography>
          </Box>
        </Stack>

        <Divider />

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <DsIcon icon="EMAIL" sx={{ fontSize: 18, color: 'text.secondary' }} />
              <DsTypography variant="body2">{contact.email}</DsTypography>
            </Stack>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <DsIcon icon="PHONE" sx={{ fontSize: 18, color: 'text.secondary' }} />
              <DsTypography variant="body2">{contact.phone}</DsTypography>
            </Stack>
          </Grid>
          {contact.workPhone && (
            <Grid item xs={12} sm={6}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <DsIcon icon="OFFICE" sx={{ fontSize: 18, color: 'text.secondary' }} />
                <DsTypography variant="body2">{contact.workPhone} (Work)</DsTypography>
              </Stack>
            </Grid>
          )}
          {contact.address && (
            <Grid item xs={12}>
              <Stack direction="row" alignItems="flex-start" spacing={1}>
                <DsIcon icon="LOCATION" sx={{ fontSize: 18, color: 'text.secondary', mt: 0.25 }} />
                <DsTypography variant="body2">
                  {contact.address.line1}
                  {contact.address.line2 && `, ${contact.address.line2}`}
                  {`, ${contact.address.city}, ${contact.address.postcode}`}
                </DsTypography>
              </Stack>
            </Grid>
          )}
        </Grid>

        {/* Permissions */}
        <Box>
          <DsTypography variant="caption" color="text.secondary" gutterBottom>
            Permissions
          </DsTypography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
            <Chip
              label="Pickup Authorized"
              size="small"
              color={contact.canPickup ? 'success' : 'default'}
              variant={contact.canPickup ? 'filled' : 'outlined'}
            />
            <Chip
              label="Emergency Contact"
              size="small"
              color={contact.isEmergencyContact ? 'error' : 'default'}
              variant={contact.isEmergencyContact ? 'filled' : 'outlined'}
            />
            <Chip
              label="Receives Reports"
              size="small"
              color={contact.receivesReports ? 'info' : 'default'}
              variant={contact.receivesReports ? 'filled' : 'outlined'}
            />
          </Stack>
        </Box>

        {/* Actions */}
        {onUpdate && (
          <Stack direction="row" spacing={1}>
            <DsButton variant="outlined" size="small" onClick={() => handleOpenEdit(contact)}>
              Edit
            </DsButton>
            {!isPrimary && (
              <DsButton variant="outlined" size="small" color="primary">
                Set as Primary
              </DsButton>
            )}
          </Stack>
        )}
      </Stack>
    </DsCard>
  );

  return (
    <Stack spacing={3}>
      {/* Summary */}
      <Grid container spacing={2}>
        <Grid item xs={6} sm={4}>
          <DsCard sx={{ p: 2, textAlign: 'center' }}>
            <DsTypography variant="h4" fontWeight={700} color="primary.main">
              {parentCarers.length}
            </DsTypography>
            <DsTypography variant="caption" color="text.secondary">
              Total Contacts
            </DsTypography>
          </DsCard>
        </Grid>
        <Grid item xs={6} sm={4}>
          <DsCard sx={{ p: 2, textAlign: 'center' }}>
            <DsTypography variant="h4" fontWeight={700} color="error.main">
              {parentCarers.filter((p) => p.isEmergencyContact).length}
            </DsTypography>
            <DsTypography variant="caption" color="text.secondary">
              Emergency Contacts
            </DsTypography>
          </DsCard>
        </Grid>
        <Grid item xs={12} sm={4}>
          <DsCard sx={{ p: 2, textAlign: 'center' }}>
            <DsTypography variant="h4" fontWeight={700} color="success.main">
              {parentCarers.filter((p) => p.canPickup).length}
            </DsTypography>
            <DsTypography variant="caption" color="text.secondary">
              Authorized Pickups
            </DsTypography>
          </DsCard>
        </Grid>
      </Grid>

      {/* Primary Contact */}
      {primaryContact && (
        <Box>
          <DsTypography variant="h6" fontWeight={600} gutterBottom>
            Primary Contact
          </DsTypography>
          {renderContactCard(primaryContact, true)}
        </Box>
      )}

      {/* Other Contacts */}
      {otherContacts.length > 0 && (
        <Box>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
            <DsTypography variant="h6" fontWeight={600}>
              Other Contacts
            </DsTypography>
            {onUpdate && (
              <DsButton
                variant="outlined"
                size="small"
                startIcon={<DsIcon icon="PLUS_CIRCLE" />}
                onClick={handleOpenAdd}
              >
                Add Contact
              </DsButton>
            )}
          </Stack>
          <Grid container spacing={2}>
            {otherContacts.map((contact) => (
              <Grid item xs={12} md={6} key={contact.id}>
                {renderContactCard(contact)}
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {/* No Contacts State */}
      {parentCarers.length === 0 && (
        <DsCard sx={{ p: 4, textAlign: 'center' }}>
          <DsIcon icon="GROUP" sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
          <DsTypography variant="h6" color="text.secondary" gutterBottom>
            No Parents/Carers Linked
          </DsTypography>
          <DsTypography variant="body2" color="text.secondary">
            Add parent or carer contact information for this pupil.
          </DsTypography>
          {onUpdate && (
            <DsButton
              variant="contained"
              size="small"
              sx={{ mt: 2 }}
              startIcon={<DsIcon icon="PLUS_CIRCLE" />}
              onClick={handleOpenAdd}
            >
              Add Contact
            </DsButton>
          )}
        </DsCard>
      )}

      {/* Add Contact Modal */}
      <Dialog open={addModalOpen} onClose={() => setAddModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Contact</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ pt: 1 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Title</InputLabel>
                  <Select
                    value={contactForm.title}
                    label="Title"
                    onChange={(e) => setContactForm({ ...contactForm, title: e.target.value })}
                  >
                    {TITLES.map((t) => (
                      <MenuItem key={t} value={t}>
                        {t}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4.5}>
                <TextField
                  fullWidth
                  size="small"
                  label="First Name"
                  value={contactForm.firstName}
                  onChange={(e) => setContactForm({ ...contactForm, firstName: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={4.5}>
                <TextField
                  fullWidth
                  size="small"
                  label="Last Name"
                  value={contactForm.lastName}
                  onChange={(e) => setContactForm({ ...contactForm, lastName: e.target.value })}
                />
              </Grid>
            </Grid>
            <FormControl fullWidth size="small">
              <InputLabel>Relationship</InputLabel>
              <Select
                value={contactForm.relationship}
                label="Relationship"
                onChange={(e) => setContactForm({ ...contactForm, relationship: e.target.value })}
              >
                {RELATIONSHIPS.map((r) => (
                  <MenuItem key={r} value={r}>
                    {r}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Email"
                  type="email"
                  value={contactForm.email}
                  onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Phone"
                  value={contactForm.phone}
                  onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })}
                />
              </Grid>
            </Grid>
            <TextField
              fullWidth
              size="small"
              label="Work Phone (optional)"
              value={contactForm.workPhone}
              onChange={(e) => setContactForm({ ...contactForm, workPhone: e.target.value })}
            />
            <Stack direction="row" spacing={2} flexWrap="wrap">
              <FormControlLabel
                control={
                  <Switch
                    checked={contactForm.isPrimaryContact}
                    onChange={(e) => setContactForm({ ...contactForm, isPrimaryContact: e.target.checked })}
                  />
                }
                label="Primary Contact"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={contactForm.isEmergencyContact}
                    onChange={(e) => setContactForm({ ...contactForm, isEmergencyContact: e.target.checked })}
                  />
                }
                label="Emergency Contact"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={contactForm.canPickup}
                    onChange={(e) => setContactForm({ ...contactForm, canPickup: e.target.checked })}
                  />
                }
                label="Can Pickup"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={contactForm.receivesReports}
                    onChange={(e) => setContactForm({ ...contactForm, receivesReports: e.target.checked })}
                  />
                }
                label="Receives Reports"
              />
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <DsButton variant="outlined" onClick={() => setAddModalOpen(false)}>
            Cancel
          </DsButton>
          <DsButton
            variant="contained"
            onClick={handleAddContact}
            disabled={!contactForm.firstName || !contactForm.lastName || !contactForm.email}
          >
            Add Contact
          </DsButton>
        </DialogActions>
      </Dialog>

      {/* Edit Contact Modal */}
      <Dialog open={editModalOpen} onClose={() => setEditModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Contact</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ pt: 1 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Title</InputLabel>
                  <Select
                    value={contactForm.title}
                    label="Title"
                    onChange={(e) => setContactForm({ ...contactForm, title: e.target.value })}
                  >
                    {TITLES.map((t) => (
                      <MenuItem key={t} value={t}>
                        {t}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4.5}>
                <TextField
                  fullWidth
                  size="small"
                  label="First Name"
                  value={contactForm.firstName}
                  onChange={(e) => setContactForm({ ...contactForm, firstName: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={4.5}>
                <TextField
                  fullWidth
                  size="small"
                  label="Last Name"
                  value={contactForm.lastName}
                  onChange={(e) => setContactForm({ ...contactForm, lastName: e.target.value })}
                />
              </Grid>
            </Grid>
            <FormControl fullWidth size="small">
              <InputLabel>Relationship</InputLabel>
              <Select
                value={contactForm.relationship}
                label="Relationship"
                onChange={(e) => setContactForm({ ...contactForm, relationship: e.target.value })}
              >
                {RELATIONSHIPS.map((r) => (
                  <MenuItem key={r} value={r}>
                    {r}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Email"
                  type="email"
                  value={contactForm.email}
                  onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Phone"
                  value={contactForm.phone}
                  onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })}
                />
              </Grid>
            </Grid>
            <TextField
              fullWidth
              size="small"
              label="Work Phone (optional)"
              value={contactForm.workPhone}
              onChange={(e) => setContactForm({ ...contactForm, workPhone: e.target.value })}
            />
            <Stack direction="row" spacing={2} flexWrap="wrap">
              <FormControlLabel
                control={
                  <Switch
                    checked={contactForm.isPrimaryContact}
                    onChange={(e) => setContactForm({ ...contactForm, isPrimaryContact: e.target.checked })}
                  />
                }
                label="Primary Contact"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={contactForm.isEmergencyContact}
                    onChange={(e) => setContactForm({ ...contactForm, isEmergencyContact: e.target.checked })}
                  />
                }
                label="Emergency Contact"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={contactForm.canPickup}
                    onChange={(e) => setContactForm({ ...contactForm, canPickup: e.target.checked })}
                  />
                }
                label="Can Pickup"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={contactForm.receivesReports}
                    onChange={(e) => setContactForm({ ...contactForm, receivesReports: e.target.checked })}
                  />
                }
                label="Receives Reports"
              />
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <DsButton variant="outlined" onClick={() => setEditModalOpen(false)}>
            Cancel
          </DsButton>
          <DsButton
            variant="contained"
            onClick={handleEditContact}
            disabled={!contactForm.firstName || !contactForm.lastName}
          >
            Save Changes
          </DsButton>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

export default ParentsCarersTab;
